import { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet'
import { Icon } from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix for default markers
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete (Icon.Default.prototype as any)._getIconUrl
Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
})

interface UserLocationMapProps {
  userLocation?: {
    latitude: number
    longitude: number
    accuracy?: number
  }
  height?: string
  showAccuracy?: boolean
}

// Component to update map center when user location changes
function MapUpdater({ userLocation }: { userLocation: { latitude: number; longitude: number } }) {
  const map = useMap()
  
  useEffect(() => {
    if (userLocation) {
      map.setView([userLocation.latitude, userLocation.longitude], 16)
    }
  }, [map, userLocation])
  
  return null
}

export function UserLocationMap({ userLocation, height = '200px', showAccuracy = true }: UserLocationMapProps) {
  const [mapReady, setMapReady] = useState(false)
  
  // Default to a central location if no user location provided
  const defaultCenter = { lat: 40.7128, lng: -74.0060 } // NYC
  const center = userLocation 
    ? { lat: userLocation.latitude, lng: userLocation.longitude }
    : defaultCenter

  useEffect(() => {
    // Small delay to ensure the map container is properly rendered
    const timer = setTimeout(() => setMapReady(true), 100)
    return () => clearTimeout(timer)
  }, [])

  if (!mapReady) {
    return (
      <div style={{ height }} className="rounded-lg overflow-hidden border border-gray-200 bg-gray-100 animate-pulse">
        <div className="flex items-center justify-center h-full">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
        </div>
      </div>
    )
  }

  return (
    <div style={{ height }} className="rounded-lg overflow-hidden border border-gray-200">
      <MapContainer
        center={[center.lat, center.lng]}
        zoom={16}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
        zoomControl={false}
        attributionControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
        />
        
        {userLocation && <MapUpdater userLocation={userLocation} />}
        
        {userLocation ? (
          <>
            {/* User location marker */}
            <Marker position={[userLocation.latitude, userLocation.longitude]}>
            </Marker>
            
            {/* Accuracy circle */}
            {showAccuracy && userLocation.accuracy && (
              <Circle
                center={[userLocation.latitude, userLocation.longitude]}
                radius={userLocation.accuracy}
                pathOptions={{
                  color: '#3b82f6',
                  fillColor: '#3b82f6',
                  fillOpacity: 0.1,
                  weight: 1
                }}
              />
            )}
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-50 bg-opacity-75 z-[1000]">
            <div className="text-center">
              <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-sm text-gray-600">Location not available</p>
            </div>
          </div>
        )}
      </MapContainer>
    </div>
  )
}